import { IgSelect, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { ExpressionNodeQueryType, UrlMatch } from "@intelligems/ig-types";
import { Grid, MenuItem } from "@mui/material";

import { useExpressionBuilderContext } from "./context";
import type { UpdateExpressionNodeQueryType } from "./utils";
import {
  AudienceConditionFilterOptions,
  calculateAllowedPageTargetingFilters,
  getAlternateFilterLabel,
  PageTargetingConditionFilterOptions,
} from "./utils";

interface ConditionFilterProps {
  configuredUrlPathFilters?: Map<UrlMatch, number>;
  onChange: UpdateExpressionNodeQueryType;
  tempExpressionQuery: ExpressionNodeQueryType;
}

export const ConditionFilter = ({
  configuredUrlPathFilters,
  onChange,
  tempExpressionQuery,
}: ConditionFilterProps) => {
  const ctx = useExpressionBuilderContext();

  const locked =
    ctx?.renderContext === "audience" &&
    (tempExpressionQuery.type === "visitor" ||
      tempExpressionQuery.type === "device");

  let filteredOptions =
    ctx?.renderContext === "audience"
      ? AudienceConditionFilterOptions
      : PageTargetingConditionFilterOptions;
  if (
    tempExpressionQuery.type === "country" ||
    tempExpressionQuery.type === "jsExpression" ||
    tempExpressionQuery.type === "trafficSource" ||
    tempExpressionQuery.type === "klaviyo"
  ) {
    filteredOptions = filteredOptions.filter(
      (f) => f.key === "equals" || f.key === "doesNotEqual"
    );
  } else if (tempExpressionQuery.type === "landingPage") {
    filteredOptions = filteredOptions.filter(
      (f) =>
        f.key === "equals" ||
        f.key === "doesNotEqual" ||
        f.key === "contains" ||
        f.key === "endsWith" ||
        f.key === "regex"
    );
  }
  if (
    tempExpressionQuery.type === "urlPath" &&
    configuredUrlPathFilters &&
    tempExpressionQuery.filter
  ) {
    const allowedFilters = calculateAllowedPageTargetingFilters(
      configuredUrlPathFilters,
      tempExpressionQuery.filter
    );
    filteredOptions = filteredOptions.map((f) => {
      if (!allowedFilters.has(f.key) && f.key !== tempExpressionQuery.filter) {
        return {
          ...f,
          disabled: true,
          tooltipText:
            "You have configured filters in this group that are incompatible.",
        };
      }
      return f;
    });
  }

  return (
    <Grid item>
      <IgSelect
        variant={"outlined"}
        value={tempExpressionQuery.filter}
        onChange={(e) => {
          onChange(e.target.value, "filter");
        }}
        disabled={locked}
      >
        {filteredOptions.map((filter) =>
          !filter.disabled ? (
            <MenuItem key={filter.key} value={filter.key}>
              <IgTypography>
                {getAlternateFilterLabel(
                  filter.key,
                  tempExpressionQuery.type
                ) || filter.title}
              </IgTypography>
            </MenuItem>
          ) : (
            <IgTooltip key={filter.key} title={filter.tooltipText}>
              <div>
                <MenuItem value={filter.key} disabled={filter.disabled}>
                  <IgTypography>
                    {getAlternateFilterLabel(
                      filter.key,
                      tempExpressionQuery.type
                    ) || filter.title}
                  </IgTypography>
                </MenuItem>
              </div>
            </IgTooltip>
          )
        )}
      </IgSelect>
    </Grid>
  );
};
